import * as path from 'path';
import * as fs from 'fs';

export interface PathValidatorOptions {
  allowSymlinks?: boolean;
  maxDepth?: number;
  blockPatterns?: RegExp[];
}

export class PathValidator {
  private normalizedAllowedPaths: string[];
  private options: PathValidatorOptions;

  constructor(
    private allowedPaths: string[],
    options: PathValidatorOptions = {}
  ) {
    this.options = {
      allowSymlinks: false,
      maxDepth: 20,
      blockPatterns: [
        /\.git\//,
        /node_modules\//,
        /\.env/,
        /\.ssh\//,
        /\.aws\//,
      ],
      ...options,
    };

    // Pre-normalize allowed paths for performance
    this.normalizedAllowedPaths = allowedPaths.map(p => 
      path.normalize(path.resolve(p))
    );
  }

  /**
   * Validate if a path is safe and allowed
   */
  isValid(filePath: string): boolean {
    if (!filePath || typeof filePath !== 'string') {
      return false;
    }

    try {
      // Check for path traversal attempts
      if (this.hasPathTraversal(filePath)) {
        return false;
      }

      // Normalize and resolve the path
      const normalizedPath = path.normalize(path.resolve(filePath));

      // Check against blocked patterns
      if (this.isBlockedPattern(normalizedPath)) {
        return false;
      }

      // Check if path is within allowed directories
      const isWithinAllowed = this.normalizedAllowedPaths.some(allowedPath => 
        normalizedPath.startsWith(allowedPath)
      );

      if (!isWithinAllowed) {
        return false;
      }

      // Check symlinks if not allowed
      if (!this.options.allowSymlinks && fs.existsSync(normalizedPath)) {
        try {
          const stats = fs.lstatSync(normalizedPath);
          if (stats.isSymbolicLink()) {
            return false;
          }
        } catch {
          return false;
        }
      }

      // Check depth limit
      if (this.options.maxDepth) {
        const depth = normalizedPath.split(path.sep).length;
        const maxAllowedDepth = Math.max(
          ...this.normalizedAllowedPaths.map(p => p.split(path.sep).length)
        ) + this.options.maxDepth;
        
        if (depth > maxAllowedDepth) {
          return false;
        }
      }

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Check for path traversal attempts
   */
  private hasPathTraversal(filePath: string): boolean {
    const dangerous = [
      '../',
      '..\\',
      '%2e%2e%2f',
      '%2e%2e/',
      '..%2f',
      '%2e%2e\\',
      '..%5c',
      '%2e%2e%5c',
      '..\\..\\',
      '../..',
    ];

    const lowerPath = filePath.toLowerCase();
    return dangerous.some(pattern => lowerPath.includes(pattern));
  }

  /**
   * Check if path matches blocked patterns
   */
  private isBlockedPattern(filePath: string): boolean {
    return this.options.blockPatterns?.some(pattern => 
      pattern.test(filePath)
    ) || false;
  }

  /**
   * Sanitize a file path for safe usage
   */
  sanitize(filePath: string): string {
    if (!filePath || typeof filePath !== 'string') {
      return '';
    }

    // Remove null bytes and control characters
    let sanitized = filePath.replace(/\0/g, '').replace(/[\x00-\x1F\x7F]/g, '');
    
    // Remove redundant slashes
    sanitized = sanitized.replace(/\/+/g, '/').replace(/\\+/g, '\\');
    
    // Remove trailing dots and spaces (Windows)
    sanitized = sanitized.replace(/[\s.]+$/g, '');
    
    // Normalize the path
    return path.normalize(sanitized);
  }

  /**
   * Get the relative path from allowed base
   */
  getRelativePath(filePath: string): string | null {
    const normalizedPath = path.normalize(path.resolve(filePath));
    
    for (const allowedPath of this.normalizedAllowedPaths) {
      if (normalizedPath.startsWith(allowedPath)) {
        return path.relative(allowedPath, normalizedPath);
      }
    }
    
    return null;
  }

  /**
   * Add a new allowed path
   */
  addAllowedPath(newPath: string): void {
    const normalized = path.normalize(path.resolve(newPath));
    if (!this.normalizedAllowedPaths.includes(normalized)) {
      this.allowedPaths.push(newPath);
      this.normalizedAllowedPaths.push(normalized);
    }
  }

  /**
   * Remove an allowed path
   */
  removeAllowedPath(removePath: string): void {
    const normalized = path.normalize(path.resolve(removePath));
    const index = this.normalizedAllowedPaths.indexOf(normalized);
    
    if (index !== -1) {
      this.allowedPaths.splice(index, 1);
      this.normalizedAllowedPaths.splice(index, 1);
    }
  }
}